import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import {
  Box, useTheme, Typography, Paper, Stack, Chip, IconButton, Tooltip,
  Collapse, Divider, alpha, Dialog, Slide, DialogTitle, DialogContent, TextField,
  InputAdornment, Grid, FormControl, InputLabel, OutlinedInput, Button,
  CircularProgress,
} from "@mui/material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SecurityIcon from "@mui/icons-material/Security";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { GetRoleList, PatchRole, GetApiLinks } from "api/role";
import PermissionEditor from "./PermissionEditor";
import PagePermissionEditor from "./PagePermissionEditor";
import NewRole from "./NewRole";
import { Add as AddIcon } from "@mui/icons-material";
import { BG_TINT } from "components/bento/bentoTokens";

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const METHOD_COLORS = {
  GET:    { bg: "#0369a1", label: "GET" },
  POST:   { bg: "#15803d", label: "POST" },
  PATCH:  { bg: "#c2410c", label: "PATCH" },
  PUT:    { bg: "#7c3aed", label: "PUT" },
  DELETE: { bg: "#be123c", label: "DELETE" },
};

const MethodChip = ({ method }) => {
  const { bg, label } = METHOD_COLORS[method] || { bg: "#374151", label: method };
  return (
    <Chip
      label={label} size="small"
      sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, bgcolor: bg, color: "#fff", borderRadius: 0.75 }}
    />
  );
};

// Group permissions by path
const groupByPath = (permissions = []) => {
  const map = {};
  permissions.forEach(({ method, path }) => {
    if (!map[path]) map[path] = [];
    map[path].push(method);
  });
  return map;
};

const RoleCard = ({ roleData, onEdit, theme }) => {
  const [expanded, setExpanded] = useState(false);
  const grouped = groupByPath(roleData.permission);
  const paths = Object.keys(grouped);
  const totalPerms = roleData.permission?.length || 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2, overflow: "hidden",
        borderColor: roleData.validflag ? theme.palette.divider : theme.palette.error.light,
      }}
    >
      {/* Header */}
      <Box
        px={2} py={1.5}
        sx={{ backgroundColor: BG_TINT, cursor: "pointer", "&:hover": { backgroundColor: alpha(theme.palette.secondary.main, 0.06) } }}
        onClick={() => setExpanded((p) => !p)}
        display="flex" alignItems="center" gap={1}
      >
        <SecurityIcon fontSize="small" color={roleData.validflag ? "secondary" : "disabled"} />
        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="body1" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
              {roleData.role}
            </Typography>
            {roleData.validflag
              ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
              : <Chip label="Inactive" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: "0.68rem" }} />
            }
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap>{roleData.description}</Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={`${totalPerms} permissions`} size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
          <Tooltip title="Edit role">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(roleData); }}
              sx={{ "&:hover": { color: theme.palette.warning.main } }}>
              <ModeEditOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Stack>
      </Box>

      {/* Permissions detail */}
      <Collapse in={expanded}>
        <Box px={2} py={1.5}>
          {paths.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No permissions defined.</Typography>
          ) : (
            <Stack spacing={0.75}>
              {paths.sort().map((path) => (
                <Stack key={path} direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary" sx={{ minWidth: 200 }}>
                    {path}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {grouped[path].sort().map((m) => <MethodChip key={m} method={m} />)}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
          {roleData.remark && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.disabled" fontStyle="italic">Remark: {roleData.remark}</Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const RoleList = () => {
  const theme = useTheme();
  const [roles, setRoles] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [apiLinks, setApiLinks] = useState([]);
  const [, setEditData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRoleDialogOpen, setNewRoleDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // edit form
  const [roleId, setRoleId] = useState("");
  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [permission, setPermission] = useState([]);
  const [pages, setPages] = useState([]);
  const [remark, setRemark] = useState("");
  const [validflag, setValidflag] = useState(true);

  const loadRoles = useCallback(() => {
    GetRoleList().then((res) => { if (res?.status === 200) { setRoles(res.data); setFiltered(res.data); } });
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  useEffect(() => {
    GetApiLinks().then((res) => { if (res?.status === 200) setApiLinks(res.data); });
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q ? roles.filter((r) => r.role?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)) : roles);
  }, [search, roles]);

  const handleEdit = (data) => {
    setEditData(data);
    setRoleId(data._id);
    setRoleName(data.role);
    setDescription(data.description || "");
    setPermission(data.permission || []);
    setPages(data.pages || []);
    setRemark(data.remark || "");
    setValidflag(data.validflag);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const cleanPerms = permission.map(({ method, path }) => ({ method, path }));
    const res = await PatchRole(roleId, roleName, description, cleanPerms, pages, remark, validflag);
    setLoading(false);
    if (res?.success) {
      Swal.fire({ title: "Updated!", icon: "success", text: `${roleName} has been updated`, timer: 1500 });
      setDialogOpen(false);
      loadRoles();
    } else {
      Swal.fire({ title: "Error!", icon: "error", text: res?.data?.message || res?.message || "Update failed" });
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <TextField
          size="small" sx={{ maxWidth: 320 }} placeholder="Search roles..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{filtered.length} role{filtered.length !== 1 ? "s" : ""}</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />}
          onClick={() => setNewRoleDialogOpen(true)}>
          New Role
        </Button>
      </Stack>

      <Dialog open={newRoleDialogOpen} onClose={() => setNewRoleDialogOpen(false)} maxWidth="md" fullWidth TransitionComponent={Transition}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          New Role
          <IconButton size="small" onClick={() => setNewRoleDialogOpen(false)}><CloseOutlinedIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <NewRole onCreated={() => { setNewRoleDialogOpen(false); loadRoles(); }} />
        </DialogContent>
      </Dialog>

      <Stack spacing={1.5} sx={{ maxHeight: "68vh", overflowY: "auto", pr: 0.5 }}>
        {filtered.map((r) => <RoleCard key={r._id} roleData={r} onEdit={handleEdit} theme={theme} />)}
        {filtered.length === 0 && (
          <Box textAlign="center" py={6} color="text.secondary">
            <SecurityIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No roles found.</Typography>
          </Box>
        )}
      </Stack>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} TransitionComponent={Transition} maxWidth="md" fullWidth onClose={() => setDialogOpen(false)}
        PaperProps={{ style: { backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary[700] : theme.palette.grey[100] } }}
      >
        <DialogTitle>
          Edit Role — <strong>{roleName?.toUpperCase()}</strong>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: "auto" }}>
        <Box component="form" onSubmit={handleSubmit} px={1} pb={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Role name</InputLabel>
                <OutlinedInput label="Role name" value={roleName} onChange={(e) => setRoleName(e.target.value)} required />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth size="small">
                <InputLabel>Description</InputLabel>
                <OutlinedInput label="Description" value={description} onChange={(e) => setDescription(e.target.value)} required />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline maxRows={3} label="Remark" value={remark} onChange={(e) => setRemark(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <Divider><Typography variant="caption" color="text.secondary">API Permissions</Typography></Divider>
            </Grid>
            <Grid item xs={12}>
              <PermissionEditor key={roleId} apiLinks={apiLinks} permission={permission} onChange={setPermission} />
            </Grid>
            <Grid item xs={12}>
              <Divider><Typography variant="caption" color="text.secondary">Page Access</Typography></Divider>
            </Grid>
            <Grid item xs={12}>
              <PagePermissionEditor pages={pages} onChange={setPages} />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="outlined" color="secondary" onClick={() => setDialogOpen(false)} disabled={loading}>Cancel</Button>
                <Button type="submit" variant="contained" color="secondary" disabled={loading}
                  startIcon={loading && <CircularProgress size={16} />}>
                  Save Changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RoleList;
